import React, { useLayoutEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import { Action as HistoryAction, Update as UpdateHistoryState } from 'history';

import PAGES from '@/constants/pages';
import { useAppDispatch } from '@/features/hooks';
import { rootActions } from '@/features/store';
import { CustomHistoryAction, THistoryState } from '@/interfaces/history.interfaces';
import { RouterType } from '@/interfaces/settings.interfaces';
import { IConfigService } from '@/services/ConfigService';
import { useInjection } from '@/services/hooks';
import { INavigationService } from '@/services/NavigationService';
import ServiceTypes from '@/services/ServiceTypes';
import getCurrentPage from '@/utils/getCurrentPage';

import { RouterProviderProps } from './RouterProvider.types';

/**
 * Провайдер для роутера с использованием NavigationService
 * @param children
 * @return {JSX.Element}
 */
function RouterProvider({ children }: RouterProviderProps) {
  const navigation = useInjection<INavigationService>(ServiceTypes.Navigation);
  const config = useInjection<IConfigService>(ServiceTypes.Config);
  const dispatch = useAppDispatch();

  const [state, setState] = useState<THistoryState>(() => {
    // https://github.com/remix-run/react-router/issues/8638
    if (config.settings.navigation.type === RouterType.Hash) {
      const match = window?.location?.hash.match(/#\w+/gi);

      if (match && match.length) {
        navigation.replace(PAGES.Cars);
      }
    }

    const currentPage = getCurrentPage(navigation.location);
    dispatch(rootActions.installation.setPage(currentPage));

    return {
      location: navigation.location,
      action: navigation.action,
    } as THistoryState;
  });

  // Подписка на изменение навигации
  useLayoutEffect(() => {
    return navigation.listen((newState: UpdateHistoryState) => {
      const { type } = config.settings.navigation;
      const isSkip = type === RouterType.Hash && newState.location.pathname?.[0] !== '/';

      if (!isSkip) {
        // Если путь не изменился, то используем
        // состояние lastPage из старого состояния
        const isPathnameMatches = state.location.pathname === newState.location.pathname;

        const currentPage = getCurrentPage(newState.location);
        const lastPage = isPathnameMatches
          ? state.location.state?.lastPage
          : getCurrentPage(state.location);

        const newStateWithLocationState: THistoryState = {
          ...newState,
          location: {
            ...newState.location,
            state: {
              ...(newState.location.state || {}),
              currentPage,
              lastPage,
            },
          },
        };

        const { action: customAction } = newStateWithLocationState.location.state || {};
        const isCustomFiltersAction =
          !!customAction &&
          [CustomHistoryAction.SetFilters, CustomHistoryAction.ResetFilters].includes(
            customAction,
          );

        setState(newStateWithLocationState);

        if (!isPathnameMatches && !isCustomFiltersAction) {
          dispatch(rootActions.installation.setPageReady(false));
          dispatch(rootActions.installation.setPage(currentPage));
        }
        // Установка фильтров в query params для определенных страниц
        if (currentPage === PAGES.Models || currentPage === PAGES.Cars) {
          switch (newStateWithLocationState.action) {
            case HistoryAction.Replace: {
              if (!isCustomFiltersAction) {
                dispatch(rootActions.choice.setFilters({}));
              }
              break;
            }
            default: {
              dispatch(rootActions.choice.setFilters({}));
              break;
            }
          }
        }
      }
    });
  }, [config.settings.navigation, dispatch, navigation, state]);

  return (
    <Router
      navigationType={state.action}
      location={state.location}
      navigator={navigation.history}
    >
      {children}
    </Router>
  );
}

export default RouterProvider;
